import { useCallback, useEffect, useRef } from 'react';

// Catalog responses must belong to the latest request in the current editor
// lifecycle. Keep drafts fresh while the network is pending; an old response
// must never restore a previous offer version or overwrite intervening edits.
const useCatalogRefreshState = (scopeKey, snapshot) => {
  const current = useRef(null);
  if (!current.current || current.current.scopeKey !== scopeKey) {
    current.current = { scopeKey, generation: 0, active: true, snapshot };
  }
  current.current.snapshot = snapshot;
  const lifecycle = current.current;

  useEffect(() => {
    lifecycle.active = true;
    return () => {
      lifecycle.active = false;
    };
  }, [lifecycle]);

  const begin = useCallback((expectedScope) => {
    const owner = current.current;
    if (!owner.active || (expectedScope != null && expectedScope !== owner.scopeKey)) return null;
    owner.generation += 1;
    return { owner, generation: owner.generation };
  }, []);

  // Standalone reads (for example, the client-pricing load triggered by a
  // client change) share the paired catalog refresh generation without
  // superseding an already-running paired request. If a newer paired refresh
  // starts while the standalone read is pending, latest() rejects its result.
  const capture = useCallback((expectedScope) => {
    const owner = current.current;
    if (!owner.active || (expectedScope != null && expectedScope !== owner.scopeKey)) return null;
    return { owner, generation: owner.generation };
  }, []);

  const read = useCallback(() => current.current.active ? current.current.snapshot : null, []);

  const latest = useCallback((request) => {
    const owner = current.current;
    return owner === request?.owner && owner.active && owner.generation === request?.generation
      ? owner.snapshot : null;
  }, []);

  return { begin, capture, latest, read };
};

export default useCatalogRefreshState;
